<template lang="pug">
  ProgramSettingsFormWrapper#activity-settings(
    :form-data="formData"
    :disable-save.sync="disableSave"
    :keys-to-reset="keysToReset"
    title="Activity Settings",
    :show-controls="true"
  )
    ContentLoader(:height="120" v-if="!programLoaded")
      rect(x="0", y="0", rx="4", ry="4", width="50%", height="15%")
      rect(x="0", y="20%", rx="4", ry="4", width="100%", height="80%")
    template(v-else)
      BField
        WtSwitchQuestionInput(
          label="Turn On Activity Converter?"
          :tooltip-text="activityConverterTooltip"
          v-model="converter"
          @input="disableSave = false"
        )
      BField
        WtButton(@click.native="modalActive = true" outlined) VIEW ACTIVITIES

      //- activities modal
      BModal(:active.sync="modalActive" has-modal-card width="100%")
        BLoading(:active.sync="isUpdating")
        .modal-card.activities-list-modal-card
          .modal-card-head
            WtTitle.is-marginless(card-title semi-bold) Converted Activities List
          .modal-card-body
            .block
              WtTitle(h6 semibold) Select which converted activities to enable:
              BCheckbox(
                v-model="selectAll"
                @input="toggleSelection($event)"
              ) Select All Activities
            .block.activities-block
              .card
                .card-content
                  WtLabel(isLabelElement text="ADA Compliant Conversions")
                  br
                  .columns.is-multiline
                    .column.is-3(
                      v-for="activity in adaActivities"
                      :key="activity.id"
                    )
                      .field
                        BCheckbox(
                          @click.native="selectAll = false"
                          v-model="activity.programEnabled"
                        ) {{ activity.name }}
            .block
              .card.card-no-box-shadow
                .card-content
                  .columns.is-multiline
                    .column.is-3(
                      v-for="activity in nonAdaActivities"
                      :key="activity.id"
                    )
                      .field
                        BCheckbox(
                          @click.native="selectAll = false"
                          v-model="activity.programEnabled"
                        ) {{ activity.name }}
          .modal-card-foot.is-block
            .level.is-mobile
              .level-left
              .level-right
                .level-item
                  WtButton(
                    is-text
                    color="red"
                    size="medium"
                    @click.native="modalActive = false"
                  ) CANCEL
                .level-item
                  WtButton(
                    is-text
                    color="lightgrey"
                    size="medium"
                    @click.native="partitionAndSetFromOriginalValues"
                  ) RESET
                .level-item
                  WtButton(
                    bold
                    color="primary"
                    size="medium"
                    @click.native="update"
                  ) SAVE
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import programSettingsFormMixin from "@/mixins/programSettingsFormMixin";
import ProgramSettingsFormWrapper from "@@/ProgramSettingsAdmin/ProgramSettingsFormWrapper";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import { ContentLoader } from "vue-content-loader";
import WtLabel from "@@/WtLabel/WtLabel";

export default {
  name: "ActivitySettings",

  components: {
    WtLabel,
    ProgramSettingsFormWrapper,
    ContentLoader,
    WtSwitchQuestionInput,
    WtButton,
    WtTitle
  },

  mixins: [programSettingsFormMixin],

  data() {
    return {
      error: false,
      modalActive: false,
      // ADA (Americans with Disabilities Act )
      adaActivities: [],
      nonAdaActivities: [],
      selectAll: false,
      isUpdating: false,
      activityConverterTooltip:
        "Allow users to convert activities (bicycling, swimming, etc) into steps."
    };
  },

  computed: {
    ...mapGetters("program", [
      "coreProgramMeta",
      "programSettingsCopy",
      "programLoaded",
      "activities"
    ]),

    formData() {
      return {
        converter: this.converter
      };
    },

    keysToReset() {
      return ["converter"];
    },

    converter: {
      set(converter) {
        this.updateProgramSettingsCopy({
          key: "converter",
          value: converter
        });
      },
      get() {
        return Number(this.programSettingsCopy.converter);
      }
    },

    enabledActivityIds() {
      const enabled = [];

      this.adaActivities.forEach(activity => {
        if (activity.programEnabled) {
          enabled.push(activity.id);
        }
      });

      this.nonAdaActivities.forEach(activity => {
        if (activity.programEnabled) {
          enabled.push(activity.id);
        }
      });

      if (enabled.length === 0) {
        // if no activities are selected, the server still
        // expects a value, sending a 0 as the only value
        // in the array will disable all activities
        enabled.push(0);
      }

      return enabled;
    }
  },

  async mounted() {
    await this.fetchActivities(this.$auth.user().programId);

    this.partitionAndSetFromOriginalValues();
  },

  methods: {
    ...mapActions("program", [
      "fetchActivities",
      "updateProgramSettingsCopy",
      "updateEnabledActivities"
    ]),
    ...mapActions(["snackBar"]),

    toggleSelection(selectAllVal) {
      this.adaActivities.forEach(activity => {
        activity.programEnabled = selectAllVal;
      });

      this.nonAdaActivities.forEach(activity => {
        activity.programEnabled = selectAllVal;
      });
    },

    partitionAndSetFromOriginalValues() {
      const partitioned = this._.partition(
        this.activities,
        activity => activity.ada
      );

      this.adaActivities = this._.cloneDeep(partitioned[0]);
      this.nonAdaActivities = this._.cloneDeep(partitioned[1]);
    },

    async update() {
      this.isUpdating = true;

      await this.updateEnabledActivities({
        programId: this.$auth.user().programId,
        activityIds: this.enabledActivityIds
      });

      this.modalActive = false;
      this.isUpdating = false;
      this.snackBar({ message: "Activities have been updated." });
    }
  }
};
</script>

<style lang="sass">
#activity-settings
  .activities-list-modal-card
    +desktop
      width: 1015px
    +tablet-only
      width: 100%
    .modal-card-head
      background: white
      border: none
    .modal-card-foot
      background: white
  .card-no-box-shadow
    box-shadow: none
  .activities-block
    margin-bottom: .5rem
</style>
